package com.bulut.mecitogullari.ui.excel

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import com.bulut.mecitogullari.core.i18n.LocalStrings
import com.bulut.mecitogullari.core.i18n.fillTemplate
import com.bulut.mecitogullari.core.importer.ExcelImportPreview
import com.bulut.mecitogullari.core.util.formatAmount
import com.bulut.mecitogullari.data.model.TransactionType
import com.bulut.mecitogullari.ui.theme.AppColors

/** Renders the whole .xlsx import dialog flow for a [ExcelImportState]. */
@Composable
fun ExcelImportDialogs(
    state: ExcelImportState,
    onConfirm: () -> Unit,
    onDismiss: () -> Unit,
) {
    when (state) {
        ExcelImportState.Hidden -> Unit

        is ExcelImportState.Preview -> ImportPreviewDialog(
            preview = state.preview,
            targetProjectName = state.targetProjectName,
            onConfirm = onConfirm,
            onDismiss = onDismiss,
        )

        ExcelImportState.Importing -> Dialog(onDismissRequest = {}) {
            Card(
                shape = RoundedCornerShape(24.dp),
                colors = CardDefaults.cardColors(containerColor = Color.White),
            ) {
                Row(
                    modifier = Modifier.padding(24.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    CircularProgressIndicator(color = AppColors.Navy)
                    Text(
                        text = LocalStrings.current.importing,
                        style = MaterialTheme.typography.bodyLarge,
                        modifier = Modifier.padding(start = 16.dp),
                    )
                }
            }
        }

        is ExcelImportState.Done -> AlertDialog(
            onDismissRequest = onDismiss,
            title = { Text(LocalStrings.current.importDoneTitle) },
            text = {
                Text(
                    LocalStrings.current.importDoneMessage
                        .fillTemplate("count" to state.transactionCount.toString())
                )
            },
            confirmButton = {
                TextButton(onClick = onDismiss) { Text(LocalStrings.current.ok) }
            },
        )

        is ExcelImportState.Failed -> AlertDialog(
            onDismissRequest = onDismiss,
            title = { Text(LocalStrings.current.importFailedTitle) },
            text = { Text(state.message) },
            confirmButton = {
                TextButton(onClick = onDismiss) { Text(LocalStrings.current.ok) }
            },
        )
    }
}

@Composable
private fun ImportPreviewDialog(
    preview: ExcelImportPreview,
    targetProjectName: String?,
    onConfirm: () -> Unit,
    onDismiss: () -> Unit,
) {
    val strings = LocalStrings.current
    Dialog(onDismissRequest = onDismiss) {
        Card(
            shape = RoundedCornerShape(24.dp),
            colors = CardDefaults.cardColors(containerColor = Color.White),
        ) {
            Column(
                modifier = Modifier
                    .verticalScroll(rememberScrollState())
                    .padding(20.dp),
                verticalArrangement = Arrangement.spacedBy(10.dp),
            ) {
                Text(strings.importPreviewTitle, style = MaterialTheme.typography.titleMedium)

                val incomeCount = preview.transactions.count { it.type == TransactionType.INCOME }
                val expenseCount = preview.transactions.size - incomeCount
                PreviewLine(
                    strings.importIncomeRows,
                    strings.importRowCount.fillTemplate("count" to incomeCount.toString()) +
                        " • ${formatAmount(preview.incomeTotal)}",
                )
                PreviewLine(
                    strings.importExpenseRows,
                    strings.importRowCount.fillTemplate("count" to expenseCount.toString()) +
                        " • ${formatAmount(preview.expenseTotal)}",
                )
                if (preview.houseExpenseCount > 0) {
                    PreviewLine(
                        strings.importHouseRows,
                        strings.importRowCount
                            .fillTemplate("count" to preview.houseExpenseCount.toString()) +
                            " • ${formatAmount(preview.houseExpenseTotal)}",
                    )
                }
                if (preview.skippedSaleCount > 0) {
                    PreviewLine(
                        strings.importSkippedSales,
                        strings.importRowCount
                            .fillTemplate("count" to preview.skippedSaleCount.toString()),
                    )
                }

                if (targetProjectName != null) {
                    Text(
                        text = strings.importTargetProject.fillTemplate("project" to targetProjectName),
                        style = MaterialTheme.typography.bodySmall,
                        color = AppColors.TextSecondary,
                    )
                }

                if (preview.unknownProjects.isNotEmpty()) {
                    HorizontalDivider(color = AppColors.Outline)
                    Text(
                        text = strings.importUnknownProjects + preview.unknownProjects.joinToString(", "),
                        style = MaterialTheme.typography.bodySmall,
                        color = AppColors.Reserved,
                    )
                }

                if (preview.errors.isNotEmpty()) {
                    HorizontalDivider(color = AppColors.Outline)
                    Text(
                        text = buildString {
                            append(
                                strings.importSkippedRows
                                    .fillTemplate("count" to preview.errors.size.toString()) + "\n"
                            )
                            append(preview.errors.take(5).joinToString("\n"))
                            if (preview.errors.size > 5) append("\n…")
                        },
                        style = MaterialTheme.typography.bodySmall,
                        color = AppColors.Expense,
                    )
                }

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    TextButton(onClick = onDismiss, modifier = Modifier.weight(1f)) {
                        Text(strings.cancel, color = AppColors.TextSecondary)
                    }
                    Button(
                        onClick = onConfirm,
                        shape = RoundedCornerShape(12.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = AppColors.Navy),
                        modifier = Modifier.weight(1f),
                    ) {
                        Text(strings.importAction, fontWeight = FontWeight.SemiBold)
                    }
                }
            }
        }
    }
}

@Composable
private fun PreviewLine(label: String, value: String) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Text(label, style = MaterialTheme.typography.bodyMedium, color = AppColors.TextSecondary)
        Text(
            text = value,
            style = MaterialTheme.typography.bodyMedium,
            fontWeight = FontWeight.Medium,
            textAlign = TextAlign.End,
            modifier = Modifier.padding(start = 12.dp),
        )
    }
}
